import {
  Badge,
  EmptyState,
  Page,
  SimplePagination,
  Table,
} from '@dixa/lib-shared-habla';
import { useEffectOnce } from 'react-use';

import { flowsApi } from 'apis/flows';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { TableFlowRow } from '../../components/tableFlowRow/tableFlowRow';
import { useScheduleFlows } from '../../utils/useScheduleFlows';

export const FlowsView = (): JSX.Element => {
  const { id: scheduleId } = useAppPathParams(
    '/settings/businesshours/:id/flows',
  );

  const { data: schedule, refetch } = flowsApi.useReadFlowScheduleQuery({
    id: scheduleId,
  });

  const {
    asyncStatus,
    currentPageFlows: flows,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = useScheduleFlows(scheduleId);

  useEffectOnce(() => {
    refetch();
  });

  return (
    <Page>
      <Page.Head title={schedule?.name ?? 'Loading'} />

      <Page.Body>
        <Page.Section
          title="Flows using this schedule"
          titleMeta={
            <Badge size="small" variant="secondary">
              {schedule?.number_of_flows || 0}
            </Badge>
          }
        >
          <Table loading={asyncStatus === 'pending'} stickyFooter stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{!!flows.length && 'Name'}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {flows.map((flow) => (
                <TableFlowRow scheduleId={scheduleId} key={flow.id} {...flow} />
              ))}
            </Table.Tbody>

            <Table.Tfoot>
              <Table.Tr>
                <Table.Td>
                  {flows.length ? (
                    <SimplePagination
                      previousButtonClick={previousPage}
                      nextButtonClick={nextPage}
                      previousButtonDisabled={!hasPreviousPage}
                      nextButtonDisabled={!hasNextPage}
                      loading={asyncStatus === 'pending'}
                    />
                  ) : (
                    <EmptyState
                      description="This schedule is currently not used in any flows."
                      icon={null}
                      title="Nothing to show"
                    />
                  )}
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
